import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, Circle, Clapperboard, Share2, Play } from "lucide-react";
import AudioListen from "@/components/AudioListen";
import { MOODS, posterFor } from "@/lib/journey";

const DoneToggle = ({ n, done, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid={`dashboard-module-done-${n}`}
    title={done ? "Mark as not done" : "Mark as done"}
    className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 transition-colors ${done ? "border-emerald-400/60 text-emerald-400 bg-emerald-400/10" : "border-[#E7DFD5] text-[#78716C] hover:border-[#F59E0B] hover:text-[#B45309]"}`}
  >
    {done ? <CheckCircle2 size={16} /> : <Circle size={16} />}
  </button>
);

const OwnedActions = ({ m, hasVideo, navigate }) => (
  <div className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-2 pt-4 border-t border-[#E7DFD5]">
    {hasVideo && (
      <button onClick={() => navigate(`/modules/${m.id}/watch`)} data-testid={`dashboard-module-watch-${m.n}`} className="btn-ember text-xs justify-center w-fit">
        <Play size={13} fill="currentColor" /> Watch video
      </button>
    )}
    <button onClick={() => navigate(`/modules/${m.id}/experience`)} data-testid={`dashboard-module-experience-${m.n}`} className="btn-outline text-xs justify-center w-fit">
      <Clapperboard size={13} /> Experience
    </button>
    <div className="flex-1">
      <AudioListen text={m.narration} label="Play the narration" testid={`dashboard-module-play-${m.n}`} analyticsKind="module" analyticsId={m.id} />
    </div>
    <span data-testid={`dashboard-module-owned-${m.n}`} className="flex items-center justify-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-400/80 px-2">
      <CheckCircle2 size={12} /> Included in your purchase
    </span>
  </div>
);

export const DashboardModuleCard = ({ m, index, hasModules, done, hasVideo, onToggleDone, onShare, onUnlock }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      data-testid={`dashboard-module-${m.n}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.04, ease: [0.65, 0, 0.35, 1] }}
      className="tacc-card group col-span-12 md:col-span-6 p-6 flex flex-col gap-4 relative overflow-hidden"
    >
      <img src={posterFor(m.id)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-45 group-hover:scale-[1.03] transition-all duration-700" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#FFFFFF] via-[#FFFFFF]/90 to-[#FFFFFF]/40" />
      <div className="relative z-10 flex items-start justify-between gap-6">
        <div className="flex items-start gap-6 flex-1 min-w-0">
          <span className="font-display text-4xl text-[#44403C] group-hover:text-[#B45309] transition-colors duration-500 shrink-0 leading-none pt-1">{m.n}</span>
          <div className="min-w-0">
            <h4 className="font-heading text-lg tracking-tight mb-1">{m.title}</h4>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C]">{m.duration} · {MOODS[m.id]} · {m.focus}</span>
            <p className="text-[#78716C] text-sm leading-relaxed mt-3">{m.snippet}</p>
          </div>
        </div>
        {hasModules ? (
          <div className="flex items-center gap-2 shrink-0 mt-1">
            {done && (
              <button onClick={onShare} data-testid={`dashboard-module-share-${m.n}`} title="Share this milestone" className="w-10 h-10 rounded-full border border-[#E7DFD5] text-[#78716C] hover:border-[#F59E0B] hover:text-[#B45309] flex items-center justify-center transition-colors">
                <Share2 size={14} />
              </button>
            )}
            <DoneToggle n={m.n} done={done} onToggle={onToggleDone} />
          </div>
        ) : (
          <span className="w-10 h-10 rounded-full border border-[#E7DFD5] flex items-center justify-center shrink-0 mt-1"><Lock size={13} /></span>
        )}
      </div>

      {hasModules ? (
        <OwnedActions m={m} hasVideo={hasVideo} navigate={navigate} />
      ) : (
        <button onClick={onUnlock} data-testid={`dashboard-module-unlock-${m.n}`} className="relative z-10 btn-outline text-xs w-fit">
          <Lock size={13} /> Unlock this module
        </button>
      )}
    </motion.div>
  );
};
